import { useEffect, useMemo, useState } from "react";

import { Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { SearchableSelect } from "~/components/ui/searchable-select";
import { Textarea } from "~/components/ui/textarea";
import {
  useCreateMaquilaRecipe,
  useUpdateMaquilaRecipe,
} from "~/hooks/maquila";
import { useOilTypes } from "~/hooks/purchasing/useOilTypes";
import { useRawMaterials } from "~/hooks/purchasing/useRawMaterials";
import type {
  MaquilaIngredientType,
  MaquilaProductRecipe,
  MaquilaRecipeFormData,
} from "~/types/maquila";
import { formatCurrency } from "~/utils/helpers";
import { costPerKgClp } from "~/utils/oilConversions";

import { MaquilaClientSelect } from "./MaquilaClientSelect";

interface MaquilaRecipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipe?: MaquilaProductRecipe | null;
}

interface LineState {
  key: string;
  ingredient_type: MaquilaIngredientType;
  oil_type_id: string | null;
  raw_material_id: string | null;
  quantity_per_unit: string;
  unit: string;
  waste_factor: string;
}

let lineCounter = 0;
const newLine = (): LineState => ({
  key: `line-${lineCounter++}`,
  ingredient_type: "raw_material",
  oil_type_id: null,
  raw_material_id: null,
  quantity_per_unit: "",
  unit: "",
  waste_factor: "1",
});

export function MaquilaRecipeDialog({
  open,
  onOpenChange,
  recipe,
}: MaquilaRecipeDialogProps) {
  const isEdit = !!recipe;
  const { data: oils = [] } = useOilTypes();
  const { data: rawMaterials = [] } = useRawMaterials();
  const createMutation = useCreateMaquilaRecipe();
  const updateMutation = useUpdateMaquilaRecipe();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const [clientId, setClientId] = useState("");
  const [productName, setProductName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [outputUnit, setOutputUnit] = useState("UNI");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineState[]>([newLine()]);

  // Populate when opening (edit vs create).
  useEffect(() => {
    if (!open) return;
    if (recipe) {
      setClientId(recipe.client_id);
      setProductName(recipe.product_name);
      setProductCode(recipe.product_code ?? "");
      setOutputUnit(recipe.output_unit || "UNI");
      setNotes(recipe.notes ?? "");
      setLines(
        (recipe.ingredients ?? []).map((ing) => ({
          key: `line-${lineCounter++}`,
          ingredient_type: ing.ingredient_type,
          oil_type_id: ing.oil_type_id,
          raw_material_id: ing.raw_material_id,
          quantity_per_unit: String(ing.quantity_per_unit ?? ""),
          unit: ing.unit,
          waste_factor: String(ing.waste_factor ?? 1),
        }))
      );
    } else {
      setClientId("");
      setProductName("");
      setProductCode("");
      setOutputUnit("UNI");
      setNotes("");
      setLines([newLine()]);
    }
  }, [open, recipe]);

  // Grouped ingredient picker: Aceites (oils) + Materia Prima (raw materials).
  const ingredientGroups = useMemo(
    () => [
      {
        label: "Aceites",
        options: oils.map((o) => ({
          value: `oil:${o.id}`,
          label: `${o.code} — ${o.name}`,
          searchTerms: `${o.code} ${o.name}`,
        })),
      },
      {
        label: "Materia Prima",
        options: rawMaterials.map((r) => ({
          value: `rm:${r.id}`,
          label: `${r.code} — ${r.name}`,
          searchTerms: `${r.code} ${r.name}`,
        })),
      },
    ],
    [oils, rawMaterials]
  );

  const unitCostFor = (line: LineState): number => {
    if (line.ingredient_type === "oil" && line.oil_type_id) {
      const oil = oils.find((o) => o.id === line.oil_type_id);
      if (!oil) return 0;
      // average_cost_clp is per-liter; recipe oils are in kg -> per-kg cost.
      return costPerKgClp(oil.average_cost_clp ?? 0, oil.density_kg_per_liter || 0.92);
    }
    if (line.ingredient_type === "raw_material" && line.raw_material_id) {
      return rawMaterials.find((r) => r.id === line.raw_material_id)?.average_cost_clp ?? 0;
    }
    return 0;
  };

  const costPerOutputUnit = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const qty = parseFloat(line.quantity_per_unit) || 0;
        const waste = parseFloat(line.waste_factor) || 1;
        return sum + qty * waste * unitCostFor(line);
      }, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, oils, rawMaterials]
  );

  const handleIngredientSelect = (key: string, value: string) => {
    const [kind, id] = value.split(":");
    setLines((prev) =>
      prev.map((l) => {
        if (l.key !== key) return l;
        if (kind === "oil") {
          return {
            ...l,
            ingredient_type: "oil",
            oil_type_id: id,
            raw_material_id: null,
            unit: "kg",
          };
        }
        const rm = rawMaterials.find((r) => r.id === id);
        return {
          ...l,
          ingredient_type: "raw_material",
          raw_material_id: id,
          oil_type_id: null,
          unit: rm?.unit || "UNI",
        };
      })
    );
  };

  const updateLine = (key: string, patch: Partial<LineState>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const removeLine = (key: string) =>
    setLines((prev) => prev.filter((l) => l.key !== key));

  const validLines = lines.filter(
    (l) =>
      (l.oil_type_id || l.raw_material_id) &&
      (parseFloat(l.quantity_per_unit) || 0) > 0
  );

  const isValid =
    !!clientId && productName.trim().length > 0 && validLines.length > 0;

  const handleSubmit = async () => {
    const formData: MaquilaRecipeFormData = {
      client_id: clientId,
      product_name: productName.trim(),
      product_code: productCode.trim() || undefined,
      output_unit: outputUnit || "UNI",
      notes: notes.trim() || undefined,
      ingredients: validLines.map((l) => ({
        ingredient_type: l.ingredient_type,
        oil_type_id: l.oil_type_id,
        raw_material_id: l.raw_material_id,
        quantity_per_unit: parseFloat(l.quantity_per_unit) || 0,
        unit: l.unit || "UNI",
        waste_factor: parseFloat(l.waste_factor) || 1,
      })),
    };

    try {
      if (isEdit && recipe) {
        await updateMutation.mutateAsync({ id: recipe.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      onOpenChange(false);
    } catch {
      // handled by mutation toast
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar receta" : "Nueva receta de maquila"}</DialogTitle>
          <DialogDescription>
            Define los insumos de Aliace (aceites y materia prima) que consume una
            unidad del producto del cliente. Al registrar un consumo se rebajan del
            inventario según la cantidad producida.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MaquilaClientSelect value={clientId} onValueChange={setClientId} />
            <div className="space-y-2">
              <Label>Unidad de salida *</Label>
              <Input
                className="h-9"
                value={outputUnit}
                onChange={(e) => setOutputUnit(e.target.value)}
                placeholder="UNI, CAJA, LT…"
              />
            </div>
            <div className="space-y-2">
              <Label>Producto (cliente) *</Label>
              <Input
                className="h-9"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Ej: ACEITE NAU BLEND PET 250ML"
              />
            </div>
            <div className="space-y-2">
              <Label>Código (opcional)</Label>
              <Input
                className="h-9"
                value={productCode}
                onChange={(e) => setProductCode(e.target.value)}
                placeholder="Código del producto del cliente"
              />
            </div>
          </div>

          {/* Ingredient lines */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Insumos por unidad</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setLines((prev) => [...prev, newLine()])}
              >
                <Plus className="mr-1 h-4 w-4" />
                Agregar insumo
              </Button>
            </div>

            <div className="space-y-2">
              {lines.map((line) => {
                const selected = line.oil_type_id
                  ? `oil:${line.oil_type_id}`
                  : line.raw_material_id
                    ? `rm:${line.raw_material_id}`
                    : "";
                return (
                  <div
                    key={line.key}
                    className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-gray-50/60 p-3 sm:grid-cols-[1fr_120px_100px_110px_36px] sm:items-end"
                  >
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Insumo</Label>
                      <SearchableSelect
                        value={selected}
                        onValueChange={(v) => handleIngredientSelect(line.key, v)}
                        groups={ingredientGroups}
                        placeholder="Buscar aceite o materia prima…"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Cant./unidad</Label>
                      <Input
                        className="h-9"
                        type="number"
                        step="0.0001"
                        min="0"
                        value={line.quantity_per_unit}
                        onChange={(e) =>
                          updateLine(line.key, { quantity_per_unit: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Unidad</Label>
                      <Input
                        className="h-9"
                        value={line.unit}
                        onChange={(e) => updateLine(line.key, { unit: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Merma (x)</Label>
                      <Input
                        className="h-9"
                        type="number"
                        step="0.01"
                        min="1"
                        value={line.waste_factor}
                        onChange={(e) =>
                          updateLine(line.key, { waste_factor: e.target.value })
                        }
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-muted-foreground hover:text-destructive"
                      onClick={() => removeLine(line.key)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Costo estimado por unidad</span>
              <span className="font-semibold">${formatCurrency(costPerOutputUnit)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Observaciones de la receta…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? "Guardar cambios" : "Crear receta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
